'use client';

import { useEffect, useId, useState, forwardRef, ForwardedRef } from 'react';
import classNames from 'classnames';

import './css/ExpandCollapse.scss';

export interface ExpandCollapseProps
  extends React.HtmlHTMLAttributes<HTMLElement> {
  buttonAriaLabel?: string;
  buttonText: string;
  buttonHeadingLevel?: 2 | 3 | 4 | 5 | 6;
  isOpen?: boolean;
}

export const ExpandCollapse = forwardRef(
  (props: ExpandCollapseProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      buttonAriaLabel,
      buttonText,
      buttonHeadingLevel,
      isOpen,
      children,
      className,
      id,
      ...spreadProps
    } = props;
    const [openState, setOpenState] = useState(isOpen || false);
    useEffect(() => {
      setOpenState(isOpen || false);
    }, [isOpen]);

    const customCollapseId = useId();
    const expandCollapseId = id || customCollapseId;
    const buttonId = `${expandCollapseId}-button`;
    const contentId = `${expandCollapseId}-content`;

    const headingAttributes = buttonHeadingLevel
      ? {
          'role': 'heading',
          'aria-level': buttonHeadingLevel,
        }
      : {};

    const expandCollapseClass = classNames({
      'ps-expand-collapse ': true,
      'ps-expand-collapse-is-open': openState,
      [`${className}`]: className,
    });

    return (
      <div
        {...spreadProps}
        id={expandCollapseId}
        className={expandCollapseClass}
        ref={ref}
      >
        <span {...headingAttributes}>
          <button
            id={buttonId}
            className="ps-expand-collapse-button"
            aria-controls={contentId}
            aria-expanded={openState ? 'true' : 'false'}
            aria-label={buttonAriaLabel}
            onClick={() => {
              setOpenState(!openState);
            }}
            type="button"
          >
            <svg
              aria-hidden="true"
              className="ps-expand-collapse-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.04 9.92992L17.12 8.99992L12.01 14.1499L6.91004 9.03992L6.00004 9.94992L11.51 15.4599C11.79 15.7399 12.24 15.7399 12.51 15.4599L18.04 9.92992Z"
                fill="#262626"
              />
            </svg>
            {buttonText}
          </button>
        </span>
        <div
          className="ps-expand-collapse-content"
          id={contentId}
          role="region"
          aria-labelledby={buttonId}
        >
          {children}
        </div>
      </div>
    );
  }
);
